import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { ComboBox } from '../../components/comboBox.tsx';
import { trpc } from '../../utils/trpc/trpc.ts';
import { MaintenanceLogDialog } from './maintenanceLogDialog.tsx';
import { EquipmentOption, SelectedPart } from './types.ts';
import { useEquipmentOptions } from './useEquipmentOptions.ts';


interface Props {
    selectedEquipment: EquipmentOption | null;
    setSelectedEquipment: (equipment: EquipmentOption | null) => void;
    selectedPart: SelectedPart | null;
    setSelectedPart: (partName: SelectedPart | null) => void;
}

export function MainControlBar({
    selectedEquipment,
    setSelectedEquipment,
    selectedPart,
    setSelectedPart,
}: Props) {

    const [isMaintenanceLogDialogOpen, setIsMaintenanceLogDialogOpen] = useState(false);


    const { equipments } = useEquipmentOptions();

    const { data: partNames } = trpc.products.getPartNames.useQuery(
        undefined,
        {
            refetchInterval: 10 * 60_000,
            select: (partNames) => [...partNames.sort()],
        }
    );

    const { data: products } = trpc.products.get.useQuery(
        undefined,
        {
            refetchInterval: 10 * 60_000,
            select: (products) => [...products.sort()],
        }
    );

    const location = useLocation();


    //Set selected equipment or product from url hash
    useEffect(() => {
        if (!equipments) return;

        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const equipment = hashParams.get('equipment');
        const partName = hashParams.get('partName');
        const product = hashParams.get('product');
        if (equipment) {
            const equipmentObj = equipments.find(it => it.id === equipment);
            setSelectedEquipment(equipmentObj ?? null);
            setSelectedPart(null);
        } else if (partName || product) {
            setSelectedEquipment(null);
            setSelectedPart({ partName, product });
        }
    }, [equipments, setSelectedEquipment, setSelectedPart, location.hash]);


    const handleEquipmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const equipment = equipments?.find(it => it.id === e.target.value);
        if (!equipment) return;
        setSelectedEquipment(equipment);
        setSelectedPart(null);
        setUrlHash('equipment=' + e.target.value);
    }

    const handlePartNameSubmit = (partName: string) => {
        if (!partName) return;
        setSelectedPart({ partName, product: null });
        setSelectedEquipment(null);
        setUrlHash('partName=' + partName);
    }

    const handleProductSubmit = (product: string) => {
        if (!product) return;
        setSelectedPart({ partName: null, product });
        setSelectedEquipment(null);
        setUrlHash('product=' + product);
    }

    const handleMaintenanceLogClick = () => {
        setIsMaintenanceLogDialogOpen(true);
    }

    const handleGuideClick = () => {
        if (!selectedEquipment) return;
        const baseUrl = 'https://berryglade.sharepoint.com/sites/BGNET/Sorvikohtaiset%20ohjeet/';
        const folder = selectedEquipment.type === 'cnc'
            ? selectedEquipment.id
            : selectedEquipment.sharepointFolder ?? '';
        const url = baseUrl + encodeURIComponent(folder);
        window.open(url, '_blank');
    }

    const setUrlHash = (hash: string) => {
        history.replaceState(null, '', document.location.pathname + '#' + hash);
    }


    const showCncViews = selectedEquipment == null || selectedEquipment.type === 'cnc';
    return (
        <>
            <div style={{ display: 'flex', gap: '8px' }}>
                <select
                    style={{ width: showCncViews ? '220px' : '280px', textOverflow: 'ellipsis' }}
                    value={selectedEquipment?.id || ''}
                    onChange={handleEquipmentChange}
                >
                    <option value={''} disabled hidden />
                    {equipments?.map(equipment => (
                        <option key={equipment.id} value={equipment.id}>{equipment.displayName}</option>
                    ))}
                </select>

                {showCncViews && <>
                    <ComboBox
                        dataId='partNameForm'
                        label='Part number'
                        defaultValue={selectedPart?.partName ?? ''}
                        suggestions={partNames ?? []}
                        onSubmit={handlePartNameSubmit}
                    />

                    <ComboBox
                        dataId='productForm'
                        label='Program name'
                        defaultValue={selectedPart?.product ?? ''}
                        suggestions={products ?? []}
                        onSubmit={handleProductSubmit}
                    />

                    <div className='flex__spacer' />
                    <button onClick={handleMaintenanceLogClick} disabled={selectedEquipment == null}>Maintenance log</button>
                    <button onClick={handleGuideClick} disabled={selectedEquipment == null}>Guide</button>
                </>}
                {!showCncViews && <>
                    <div className='flex__spacer' />
                    <button onClick={handleGuideClick} disabled={selectedEquipment == null}>Guide</button>
                </>}
            </div>
            {(selectedEquipment && isMaintenanceLogDialogOpen) &&
                <MaintenanceLogDialog
                    equipment={selectedEquipment.id}
                    onClose={() => setIsMaintenanceLogDialogOpen(false)}
                />
            }
        </>
    )
}
